import { Box, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router";
import { Breadcrumbs, BreadcrumbType } from "src/components/Breadcrumb/Breadcrumbs";
import { GridPageHeader } from "src/components/Grid/PageGridHeader";
import { URLHandler } from "src/services/URLHandler";
import { PropsWithChildren, useMemo } from "react";

export type CMSPageLayoutProps = PropsWithChildren<{
    title: string;
    extraBreadcrumbs?: BreadcrumbType[];
}>;

export const CMSPageLayout = ({ title, extraBreadcrumbs = [], children }: CMSPageLayoutProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();

    const isPdfMode = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.get("__pdf") === "1";
    }, [location.search]);

    const path = location.pathname || "/";
    const segments = path.split("/").filter(Boolean);

    const formatSegment = (seg: string) => {
        try {
            const decoded = decodeURIComponent(seg);
            return decoded
                .replace(/[-_]+/g, " ")
                .replace(/\s+/g, " ")
                .trim()
                .replace(/\b\w/g, (c) => c.toUpperCase());
        } catch {
            return seg;
        }
    };

    const segmentCrumbs: BreadcrumbType[] = segments.slice(0, -1).map((seg, idx) => {
        const href = "/" + segments.slice(0, idx + 1).join("/");
        return {
            label: formatSegment(seg),
            action: () => navigate(href),
        };
    });

    const breadcrumbs: BreadcrumbType[] = [
        {
            label: t("cms.home", "Home"),
            action: () => navigate(URLHandler.home()),
        },
        ...extraBreadcrumbs,
        ...segmentCrumbs,
        {
            label: title,
        },
    ];

    return (
        <Box sx={{ p: isPdfMode ? 0 : theme.spacing(4, 6) }}>
            {!isPdfMode && (
                <Box sx={{ mb: theme.spacing(4) }}>
                    <GridPageHeader title={title} />
                    <Box sx={{ mt: theme.spacing(2) }}>
                        <Breadcrumbs items={breadcrumbs} />
                    </Box>
                </Box>
            )}
            {children}
        </Box>
    );
};

export default CMSPageLayout;
